import { db } from '../../../integrations/firebase';
import { ref, push, serverTimestamp } from 'firebase/database';

/**
 * Logs an internal timeline event for an order.
 * Call this whenever an action is performed on an order.
 *
 * @param {string} firebaseKey - The Firebase key of the order
 * @param {object} event - Event details
 * @param {string} event.action - What happened (e.g., "Moved to Shiprocket", "Label Generated", "Pincode Updated")
 * @param {string} event.agent - Who did it (e.g., "Aneesh T", "System", "Admin")
 * @param {string} [event.details] - Additional details (e.g., "AWB: SRSC123", "Pincode: 680001 → 682001")
 *
 * @example
 * import { logInternalEvent } from '../utils/internalTimeline';
 * await logInternalEvent('orderKey123', {
 *   action: 'Moved to Shiprocket',
 *   agent: 'Aneesh T',
 *   details: 'Shiprocket Order #SR12345'
 * });
 */
export const logInternalEvent = async (firebaseKey, { action, agent, details }) => {
  if (!firebaseKey || !action) {
    console.warn('[InternalTimeline] Missing firebaseKey or action');
    return;
  }

  try {
    const timelineRef = ref(db, `orders/${firebaseKey}/internalTimeline`);
    await push(timelineRef, {
      action,
      agent: agent || 'System',
      details: details || '',
      timestamp: new Date().toISOString(),
    });
    console.log(`[InternalTimeline] Logged: ${action} by ${agent} for ${firebaseKey}`);
  } catch (error) {
    console.error('[InternalTimeline] Failed to log event:', error);
  }
};

/**
 * Common internal timeline actions — use these constants to keep naming consistent.
 */
export const INTERNAL_ACTIONS = {
  ORDER_CREATED: 'Order Created',
  ORDER_UPDATED: 'Order Updated',
  STATUS_CHANGED: 'Status Changed',
  MOVED_TO_SHIPROCKET: 'Moved to Shiprocket',
  MOVED_TO_DTDC: 'Moved to DTDC',
  MOVED_TO_INDIAPOST: 'Booked via India Post',
  AWB_ASSIGNED: 'AWB Assigned',
  LABEL_GENERATED: 'Label Generated',
  LABEL_PRINTED: 'Label Printed',
  PICKUP_SCHEDULED: 'Pickup Scheduled',
  PICKUP_COMPLETED: 'Pickup Completed',
  PINCODE_UPDATED: 'Pincode Updated',
  ADDRESS_UPDATED: 'Address Updated',
  COMBO_CHANGED: 'Combo/Products Changed',
  PAYMENT_UPDATED: 'Payment Updated',
  CUSTOMER_UPDATED: 'Customer Info Updated',
  CANCELLED: 'Order Cancelled',
  TRACKING_SYNCED: 'Tracking Synced',
  NDR_ACTION: 'NDR Action Taken',
  CALL_NOTE_ADDED: 'Call Note Added',
  RTO_INITIATED: 'RTO Initiated',
  DELIVERED: 'Delivered',

  // Pre-Shipment Desk actions
  COD_CONFIRMED: 'COD Confirmed',
  COD_REJECTED: 'COD Rejected',
  COD_NO_RESPONSE: 'COD No Response',
  COD_CALL_LATER: 'COD Call Later',
  COD_WRONG_NUMBER: 'COD Wrong Number',
  REMARK_ADDED: 'Remark Added',
  REMARK_UPDATED: 'Remark Updated',
  COURIER_ASSIGNED: 'Courier Assigned',
  MOVED_TO_COURIER_MANAGER: 'Moved to Courier Manager',
  FOLLOWUP_CREATED: 'Follow-up Created',
  FOLLOWUP_COMPLETED: 'Follow-up Completed',
  ADMIN_OVERRIDE: 'Admin Override Used',
  HOLD_SET: 'Order Set to Hold',
};
